import {
  Component,
  forwardRef,
  ViewChild,
  ElementRef,
  Output,
  EventEmitter,
  Input,
  OnInit,
} from "@angular/core";
import {
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
  FormGroup,
  FormBuilder,
  Validators,
} from "@angular/forms";
import { MedicalOrderEdit } from "../../../../model/medical-order-edit";
import { FilterCompleteService } from "../../../../filter-complete/filter-complete";
import { MedicalOrderState } from "../../../../model/medical-order-state";
import { ValidateAutoCompleteSelect } from "../../../../validators/autocomplete-select.validator";
import { NotificationToastService } from "../../../../services/notification-toast.service";
import { ReferringPhysicianFilter } from "src/app/model/referring-physician";
import { ReferringPhysicianCompleteComponent } from "../../../autocomplete/referring-physician/referring-physician-autocomplete.component";
import {
  medicalOrderDateValidator,
  medicalOrderCredentialNumberValidator,
  medicalOrderDiagnosticValidator,
  medicalOrderDiagnosisCIEValidator,
} from "../../../../validators/medical-order-edit.validator";
import { ValidationType } from "src/app/enums/validation-type.enum";
import { SectorType } from "../../../../enums/sector-type.enum";
import { AuthService } from "../../../../services/auth.service";
import { ReferringPhysicianInstituteComponent } from "../../../autocomplete/referring-physician-institute/referring-physician-institute.component";
import { ReferringPhysicianInstituteFilter } from "../../../../model/referring-physician-institute-filter";
import { DiagnosticService } from "src/app/services/diagnostic.service";
import { DiagnosticFilter } from "src/app/model/diagnostic-filter";

export const MEDICAL_ORDER_EDIT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MedicalOrderEditControlComponent),
  multi: true,
};

@Component({
  selector: "app-medical-order-edit-control-component",
  providers: [MEDICAL_ORDER_EDIT_CONTROL_VALUE_ACCESSOR],
  templateUrl: "./medical-order-edit-control.component.html",
  styleUrls: ["./medical-order-edit-control.component.css"],
})
export class MedicalOrderEditControlComponent implements ControlValueAccessor {
  filterCompleteautMedicalOrderState: FilterCompleteService<MedicalOrderState>;
  filterCompleteautDiagnosisCIE: FilterCompleteService<DiagnosticFilter>;
  form: FormGroup;
  athorizationValidity: boolean;
  loading = true;
  isPatientAmountVisible: boolean;
  isMedicalOrderObservationEditable: boolean;
  isPatientObservationEditable: boolean;
  transactionReferenceMessage: string;
  transactionReferenceNumber: string;
  medicalAppointmentId: number;
  validateOnLineVisible = false;
  validateOnLineRequired = false;
  validateOnLineNeedCancel = false;
  nroFormValidate = false;
  canViewMedicaOrderDate: boolean;
  canViewReferringPhysician: boolean;
  enabledEdition: boolean;
  freeDiagnosisVisible: boolean = true;
  CIEDiagnosisVisible: boolean = true;
  validating: boolean;
  validityDays = "";
  securityCodeValidate: boolean;
  alertSecurityCodeValidate: boolean;
  preAuthorizationValidate: boolean;
  onChange;
  referringPhysicianInstituteVisible: boolean;
  isDisabled: boolean;
  existsMedicalAppointmentValidation: boolean;

  @Input() isReadOnly: boolean;
  @Input() disabledValidationOnLine: boolean;
  @Input() isBillingControl: boolean;
  @ViewChild("referringPhysician", { static: false })
  referringPhysician: ReferringPhysicianCompleteComponent;
  @ViewChild("referringPhysicianInstitute", { static: false })
  referringPhysicianInstitute: ReferringPhysicianInstituteComponent;
  @ViewChild("credentialNumber", { static: false })
  credentialNumber: ElementRef;

  // tslint:disable-next-line:no-output-on-prefix
  @Output() onExecuteOnLineOperationClick: EventEmitter<boolean> =
    new EventEmitter();

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private diagnosticService: DiagnosticService,
    private notificationToastService: NotificationToastService
  ) {
    this.securityCodeValidate = false;
    this.alertSecurityCodeValidate = false;
    this.preAuthorizationValidate = false;
    this.loadForm();
  }

  private loadForm() {
    this.form = this.formBuilder.group(
      {
        MedicalOrderEditInfo: [null, Validators.required],
        PatientObservation: [null],
        OrderObservation: [null],
        MedicalOrder: this.formBuilder.group({
          ReferringPhysician: [
            null,
            [Validators.required, ValidateAutoCompleteSelect],
          ],
          ReferringPhysicianInstitute: [null],
          MedicalOrderState: [
            null,
            [Validators.required, ValidateAutoCompleteSelect],
          ],
          DiagnosticRequired: [null],
          CredentialNumber: [null],
          SecurityCodeCredentialNumber: [null],
          Diagnostic: [null],
          DiagnosisCIE: [null],
          DiagnosisId: [null],
          PatientYearOld: [null],
          PrescriptionDate: [null],
          Id: [null],
          ReferringPhysicianId: [null, Validators.required],
          ReferringPhysicianInstituteId: [null],
          MedicalOrderStateId: [null, Validators.required],
          PatientId: [null, Validators.required],
          ProtocolId: [null],
          SectorId: [null],
          TransitStateId: [null],
          MedicalOrdenBatchId: [null],
          PlanId: [null],
          MedicalOrderAmount: [null],
          IncompleteMedicalOrderAmount: [null],
          AutorizationDate: [null],
          LastMedicalOrdenBatchItemId: [null],
          OriginalEntityValue: [null],
          CreateDate: [null],
          CreateUser: [null],
          UpdateDate: [null],
          UpdateUser: [null],
          NroPreAuthorization: [null],
          CanViewMedicaOrderDate: [true],
          CanViewReferringPhysician: [true],
        }),
      },
      {
        validator: [
          medicalOrderDateValidator.bind(this),
          medicalOrderDiagnosticValidator.bind(this),
          medicalOrderCredentialNumberValidator.bind(this),
          medicalOrderDiagnosisCIEValidator.bind(this),
        ],
      }
    );

    this.medicalOrder.DiagnosisCIE.valueChanges.subscribe(
      (diag: DiagnosticFilter) => {
        (<FormGroup>this.form.get("MedicalOrder")).patchValue({
          DiagnosisId: diag ? diag.Id : null,
        });
      }
    );

    this.medicalOrder.PrescriptionDate.valueChanges.subscribe((date: Date) => {
      if (this.athorizationValidity) {
        (<FormGroup>this.form.get("MedicalOrder")).patchValue({
          AutorizationDate: date,
        });
      }
    });

    this.medicalOrder.ReferringPhysician.valueChanges.subscribe(
      (value: ReferringPhysicianFilter) => {
        (<FormGroup>this.form.get("MedicalOrder")).patchValue({
          ReferringPhysicianId: null,
        });
        if (value) {
          (<FormGroup>this.form.get("MedicalOrder")).patchValue({
            ReferringPhysicianId: value.Id,
          });
        }
      }
    );

    this.medicalOrder.ReferringPhysicianInstitute.valueChanges.subscribe(
      (value: ReferringPhysicianInstituteFilter) => {
        (<FormGroup>this.form.get("MedicalOrder")).patchValue({
          ReferringPhysicianInstituteId: null,
        });
        if (value) {
          (<FormGroup>this.form.get("MedicalOrder")).patchValue({
            ReferringPhysicianInstituteId: value.Id,
          });
        }
      }
    );

    this.medicalOrder.MedicalOrderState.valueChanges.subscribe(
      (value: MedicalOrderState) => {
        (<FormGroup>this.form.get("MedicalOrder")).patchValue({
          MedicalOrderStateId: null,
        });
        if (value) {
          (<FormGroup>this.form.get("MedicalOrder")).patchValue({
            MedicalOrderStateId: value.Id,
          });
          this.isPatientAmountVisible = value.IsPatientAmountVisible;
          if (!this.isPatientAmountVisible) {
            (<FormGroup>this.form.get("MedicalOrder")).patchValue({
              IncompleteMedicalOrderAmount: null,
            });
          }
        }
      }
    );

    this.filterCompleteautDiagnosisCIE =
      new FilterCompleteService<DiagnosticFilter>(
        [],
        this.medicalOrder.DiagnosisCIE
      );

    this.filterCompleteautMedicalOrderState =
      new FilterCompleteService<MedicalOrderState>(
        [],
        this.medicalOrder.MedicalOrderState
      );

    this.form.valueChanges.subscribe(() => {
      this.medicalOrderEditChange();
    });

    this.setCanViewMedicaOrderDate();
    this.setCanViewReferringPhysician();
  }

  private async setCanViewReferringPhysician() {
    this.canViewReferringPhysician =
      await this.authService.canViewReferringPhysician();
    if (!this.canViewReferringPhysician) {
      (<FormGroup>this.form.get("MedicalOrder"))
        .get("ReferringPhysician")
        .setValidators([]);
      (<FormGroup>this.form.get("MedicalOrder"))
        .get("ReferringPhysicianId")
        .setValidators([]);
      (<FormGroup>this.form.get("MedicalOrder"))
        .get("ReferringPhysician")
        .setErrors(null);
      (<FormGroup>this.form.get("MedicalOrder"))
        .get("ReferringPhysicianId")
        .setErrors(null);
    }
  }

  private async setCanViewMedicaOrderDate() {
    this.canViewMedicaOrderDate =
      await this.authService.canViewMedicaOrderDate();
  }

  executeOnLineCancelOperation(): void {
    this.onExecuteOnLineOperationClick.emit(false);
  }

  executeOnLineOperation(): void {
    if (
      this.medicalOrder.CredentialNumber.value &&
      (!this.securityCodeValidate ||
        this.alertSecurityCodeValidate ||
        this.medicalOrder.SecurityCodeCredentialNumber.value) &&
      this.medicalOrder.ReferringPhysician.value &&
      (!this.CIEDiagnosisVisible || this.medicalOrder.DiagnosisCIE.value)
    ) {
      this.onExecuteOnLineOperationClick.emit(true);
    } else if (!this.medicalOrder.CredentialNumber.value) {
      this.notificationToastService.error(
        "Debe ingresar el número de afiliado",
        "Validación on line"
      );
    } else if (!this.medicalOrder.SecurityCodeCredentialNumber.value) {
      this.alertSecurityCodeValidate = true;
      this.notificationToastService.error(
        "Debe ingresar el código de seguridad",
        "Validación on line"
      );
    } else if (!this.medicalOrder.ReferringPhysician.value) {
      this.notificationToastService.error(
        "Debe ingresar el médico derivante",
        "Validación on line"
      );
    } else if (this.CIEDiagnosisVisible && !this.medicalOrder.DiagnosisCIE.value) {
      this.notificationToastService.error(
        "Debe ingresar el diagnóstico CIE",
        "Validación on line"
      );
    }
  }

  invalidAthorizationValidity(): boolean {
    return this.form.errors
      ? this.form.errors.invalidAthorizationValidity
      : false;
  }

  get invalid() {
    this.medicalOrder.CredentialNumber.markAsTouched();
    return this.form.invalid;
  }

  get medicalOrder() {
    return (<FormGroup>this.form.get("MedicalOrder")).controls;
  }

  get filteredOrderStatesOptions() {
    return this.filterCompleteautMedicalOrderState
      ? this.filterCompleteautMedicalOrderState.filteredOptions
      : [];
  }

  get filteredDiagnosesCIEOptions() {
    return this.filterCompleteautDiagnosisCIE
      ? this.filterCompleteautDiagnosisCIE.filteredOptions
      : [];
  }

  get f() {
    return this.form.controls;
  }

  medicalOrderEditChange() {
    const medicalOrderEdit = <MedicalOrderEdit>this.form.getRawValue();

    if (this.onChange) {
      this.onChange(medicalOrderEdit);
    }
  }

  async writeValue(obj: MedicalOrderEdit) {
    await this.setCanViewMedicaOrderDate();
    await this.setCanViewReferringPhysician();

    if (obj) {
      this.filterCompleteautMedicalOrderState =
        new FilterCompleteService<MedicalOrderState>(
          obj.MedicalOrderEditInfo.MedicalOrderStates,
          this.medicalOrder.MedicalOrderState
        );

      const diagnoses = await this.diagnosticService.getDiagnosticsFilter();
      const diagnosis = diagnoses.find(
        (d) => d.Id === obj.MedicalOrder.DiagnosisId
      );

      this.filterCompleteautDiagnosisCIE =
        new FilterCompleteService<DiagnosticFilter>(
          diagnoses,
          this.medicalOrder.DiagnosisCIE
        );

      this.securityCodeValidate =
        obj.MedicalOrderEditInfo.SecurityCodeCredentialNumberValidate;

      this.preAuthorizationValidate =
        obj.MedicalOrderEditInfo.EntityValidator != null
          ? obj.MedicalOrderEditInfo.EntityValidator.PreAuthorizationRequired
          : false;

      this.setValidationInfo(obj);

      this.freeDiagnosisVisible = !obj.MedicalOrderEditInfo.DiagnosticCIERequired;
      this.CIEDiagnosisVisible = obj.MedicalOrderEditInfo.DiagnosticCIERequired;

      this.form.patchValue({
        MedicalOrderEditInfo: obj.MedicalOrderEditInfo,
        PatientObservation: obj.PatientObservation,
        OrderObservation: obj.OrderObservation,
      });

      this.referringPhysicianInstituteVisible =
        obj.MedicalOrderEditInfo.ReferringPhysicianInstituteRequired;

      (<FormGroup>this.form.get("MedicalOrder")).patchValue({
        DiagnosisCIE: diagnosis,
        DiagnosisId: obj.MedicalOrder.DiagnosisId,
        ReferringPhysician: this.canViewReferringPhysician
          ? obj.MedicalOrderEditInfo.ReferringPhysician
          : null,
        ReferringPhysicianInstitute:
          obj.MedicalOrderEditInfo.ReferringPhysicianInstitute,
        MedicalOrderState: obj.MedicalOrderEditInfo.MedicalOrderState,
        CredentialNumber: obj.MedicalOrder.CredentialNumber,
        SecurityCodeCredentialNumber:
          obj.MedicalOrder.SecurityCodeCredentialNumber,
        NroPreAuthorization: obj.MedicalOrder.NroPreAuthorization,
        DiagnosticRequired: obj.MedicalOrderEditInfo.DiagnosticRequired,
        Diagnostic: obj.MedicalOrder.Diagnostic,
        PatientYearOld: obj.MedicalOrder.PatientYearOld,
        PrescriptionDate: this.canViewMedicaOrderDate
          ? obj.MedicalOrder.PrescriptionDate
          : new Date(),
        Id: obj.MedicalOrder.Id,
        ReferringPhysicianId: this.canViewReferringPhysician
          ? obj.MedicalOrder.ReferringPhysicianId
          : null,
        ReferringPhysicianInstituteId:
          obj.MedicalOrder.ReferringPhysicianInstituteId,
        MedicalOrderStateId: obj.MedicalOrder.MedicalOrderStateId,
        PatientId: obj.MedicalOrder.PatientId,
        ProtocolId: obj.MedicalOrder.ProtocolId,
        SectorId: obj.MedicalOrder.SectorId,
        OriginalEntityValue: obj.MedicalOrder.OriginalEntityValue,
        TransitStateId: obj.MedicalOrder.TransitStateId,
        MedicalOrdenBatchId: obj.MedicalOrder.MedicalOrdenBatchId,
        PlanId: obj.MedicalOrder.PlanId,
        MedicalOrderAmount: obj.MedicalOrder.MedicalOrderAmount,
        IncompleteMedicalOrderAmount: obj.MedicalOrderEditInfo.MedicalOrderState
          .IsPatientAmountVisible
          ? obj.MedicalOrder.IncompleteMedicalOrderAmount
          : null,
        AutorizationDate: obj.MedicalOrder.AutorizationDate,
        LastMedicalOrdenBatchItemId:
          obj.MedicalOrder.LastMedicalOrdenBatchItemId,
        CreateDate: obj.MedicalOrder.CreateDate,
        CreateUser: obj.MedicalOrder.CreateUser,
        UpdateDate: obj.MedicalOrder.UpdateDate,
        UpdateUser: obj.MedicalOrder.UpdateUser,
        CanViewMedicaOrderDate: this.canViewMedicaOrderDate,
        CanViewReferringPhysician: this.canViewReferringPhysician,
      });

      this.athorizationValidity = obj.MedicalOrderEditInfo.AthorizationValidity;

      if (this.athorizationValidity) {
        this.validityDays =
          "(" + obj.MedicalOrderEditInfo.AthorizationValidityDay + ")";
      } else {
        this.validityDays =
          "(" + obj.MedicalOrderEditInfo.MedicalOrderValidityDay + ")";
      }

      this.isPatientAmountVisible =
        obj.MedicalOrderEditInfo.IsPatientAmountVisible;
      this.isMedicalOrderObservationEditable =
        obj.MedicalOrderEditInfo.IsMedicalOrderObservationEditable;
      this.isPatientObservationEditable =
        obj.MedicalOrderEditInfo.IsPatientObservationEditable;
    }
  }

  setValidationInfo(obj: MedicalOrderEdit) {
    this.validating = false;
    this.validateOnLineNeedCancel = false;
    this.enabledEdition = this.isBillingControl;

    if (obj.MedicalOrderEditInfo.MedicalAppointmentValidation) {
      this.existsMedicalAppointmentValidation = true;

      this.medicalAppointmentId =
        obj.MedicalOrderEditInfo.MedicalAppointmentValidation.Id;

      this.transactionReferenceMessage =
        obj.MedicalOrderEditInfo.MedicalAppointmentValidation.TransactionReferenceMessage;

      this.transactionReferenceNumber =
        obj.MedicalOrderEditInfo.MedicalAppointmentValidation.TransactionReferenceNumber;
    }

    if (
      obj.MedicalOrderEditInfo.MedicalAppointmentValidation &&
      obj.MedicalOrderEditInfo.MedicalAppointmentValidation.NeedCancel
    ) {
      let needCancel = true;

      if (obj.MedicalOrderEditInfo.MedicalAppointmentValidation) {
        needCancel =
          obj.MedicalOrderEditInfo.MedicalAppointmentValidation.NeedCancel;
      }

      this.validateOnLineNeedCancel =
        needCancel &&
        obj.MedicalOrderEditInfo.EntityValidator &&
        obj.MedicalOrderEditInfo.EntityValidator.OperationTypeId !==
          ValidationType.VerificarAfiliado;
    }

    if (
      obj.MedicalOrderEditInfo.EntityValidator &&
      obj.MedicalOrderEditInfo.EntityValidator.ValidationOnLineRequired &&
      !this.disabledValidationOnLine &&
      (!this.validateOnLineNeedCancel || this.isBillingControl)
    ) {
      this.validateOnLineVisible = true;
      if (!this.isBillingControl) {
        this.validateOnLineRequired = true;
      }
    } else {
      this.validateOnLineRequired = false;
      this.validateOnLineVisible = false;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {}

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  setFocus() {
    if (this.referringPhysician && !this.medicalOrder.CredentialNumber.value) {
      this.referringPhysician.setFocus();
    } else {
      this.credentialNumber.nativeElement.focus();
    }
  }
}
